import { create } from "zustand";
import api from "../services/api";
import sharedAuthService from "../services/sharedAuth.service";
import { resolvePolicySubjectId } from "../utils/policySubjectId";

function normalizeParentsPayload(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out = {};
  for (const [key, value] of Object.entries(raw)) {
    const k = String(key || "").trim();
    if (!k) continue;
    const list = Array.isArray(value) ? value : [value];
    out[k] = list.map((p) => String(p ?? "").trim()).filter(Boolean);
  }
  return out;
}

function extractPolicyPayload(resData) {
  const payload = resData ?? {};
  if (payload.policies && typeof payload.policies === "object") {
    return {
      policies: payload.policies,
      parents: normalizeParentsPayload(payload.parents),
      state: payload.state && typeof payload.state === "object" ? payload.state : null,
    };
  }
  if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    return {
      policies: payload.data,
      parents: normalizeParentsPayload(payload.parents),
      state: payload.state && typeof payload.state === "object" ? payload.state : null,
    };
  }
  if (typeof payload === "object" && !Array.isArray(payload)) {
    const hasPolicyShape = Object.values(payload).some((v) => typeof v === "boolean");
    if (hasPolicyShape) {
      return { policies: payload, parents: {}, state: null };
    }
  }
  return { policies: {}, parents: {}, state: null };
}

/** Log GET /policy/me cache as one expandable console object. */
function logPolicyCacheToConsole(policies, { studyScope } = {}) {
  if (typeof console === "undefined" || !console.log) return;

  const sortedPolicies = {};
  for (const key of Object.keys(policies || {})
    .filter((k) => String(k).trim())
    .sort((a, b) => a.localeCompare(b))) {
    sortedPolicies[key] = policies[key];
  }

  console.log("[Policy cache]", {
    study: studyScope || null,
    policies: sortedPolicies,
  });
}

// Zustand cache from GET /policy/me (sidebar / tab hints / stage button hints).
// Realtime enforcement: business GET with guard flags (*Viewer=true, openIntakeStage=true, …)
// → policyGuard → evaluateUnifiedPolicy (fresh). Mutations use POST/PATCH on same routes.
export const usePolicyStore = create((set, get) => {
  let fetchPoliciesGeneration = 0;
  let inFlightFetchKey = "";
  let inFlightFetchPromise = null;
  let lastLoadedFetchKey = "";

  return {
    policies: {},
    /** @type {Record<string, string[]>} immediate parent cache keys per child key (from Hub IAM) */
    actionParents: {},
    evaluationState: null,
    loaded: false,
    loading: false,
    loadingScope: null,
    /** After the first successful /policy/me during app open; study changes show "Switching resource". */
    initialLoadComplete: false,

    /**
     * Load permissions from GET /policy/me.
     * @param {{ studyId?: string, force?: boolean, silent?: boolean }} [opts]
     *   - studyId: scopes tmf_role to matching resource_access row
     *   - force: bypass the "already loaded this key" short-circuit
     *   - silent: refresh in the background without flipping `loading` (so focus
     *     refreshes don't flash the "Switching resource" spinner)
     */
    fetchPolicies: async (opts = {}) => {
      const currentUser = sharedAuthService.getCurrentUser();
      const subjectId = resolvePolicySubjectId(currentUser);
      const fromOpts =
        opts?.studyId != null && String(opts.studyId).trim() !== ""
          ? String(opts.studyId).trim()
          : "";
      const fromLs =
        typeof localStorage !== "undefined" && localStorage.getItem("selectedStudyId")
          ? String(localStorage.getItem("selectedStudyId")).trim()
          : "";
      const studyScope = fromOpts || fromLs;
      const requestKey = `${String(subjectId || "")}|${studyScope || ""}`;
      const force = opts?.force === true;
      const silent = opts?.silent === true;

      if (!force && lastLoadedFetchKey === requestKey && get().loaded && !get().loading) {
        return;
      }
      if (inFlightFetchKey === requestKey && inFlightFetchPromise) {
        return inFlightFetchPromise;
      }

      const myGen = ++fetchPoliciesGeneration;

      const run = async () => {
        let loadingStartedAt = 0;
        try {
          if (!silent) {
            set({ loading: true, loadingScope: studyScope || null });
          }
          loadingStartedAt = Date.now();

          const queryParams = {};
          if (process.env.NODE_ENV !== "production") {
            queryParams.with_state = "1";
          }
          if (subjectId) queryParams.subject_id = String(subjectId);
          if (studyScope) queryParams.study_id = studyScope;
          const requestConfig = { params: queryParams };

          let res;
          try {
            res = await api.get("/policy/me", requestConfig);
          } catch (err) {
            if (err?.response?.status === 401 && subjectId) {
              res = await api.get("/policy/me", requestConfig);
            } else if (err?.response?.status === 404) {
              try {
                res = await api.get("/policies/me", requestConfig);
              } catch (fallbackErr) {
                if (fallbackErr?.response?.status === 401 && subjectId) {
                  res = await api.get("/policies/me", requestConfig);
                } else {
                  throw fallbackErr;
                }
              }
            } else {
              throw err;
            }
          }

          if (myGen !== fetchPoliciesGeneration) return;

          const { policies, parents, state } = extractPolicyPayload(res?.data);

          const rawEvaluationState = state;
          const normalizedEvaluationState = rawEvaluationState
            ? (() => {
                const appUserAttrs = Array.isArray(rawEvaluationState.local_app_user_attributes)
                  ? rawEvaluationState.local_app_user_attributes
                  : Array.isArray(rawEvaluationState.local_tmf_user_attributes)
                    ? rawEvaluationState.local_tmf_user_attributes
                    : [];
                const merged = {
                  ...rawEvaluationState,
                  local_app_user_attributes: appUserAttrs,
                  action_parents:
                    rawEvaluationState.action_parents &&
                    typeof rawEvaluationState.action_parents === "object"
                      ? normalizeParentsPayload(rawEvaluationState.action_parents)
                      : parents,
                };
                delete merged.local_tmf_user_attributes;
                return merged;
              })()
            : null;

          const mergedParents =
            Object.keys(parents).length > 0
              ? parents
              : normalizeParentsPayload(normalizedEvaluationState?.action_parents);

          set({
            policies,
            actionParents: mergedParents,
            evaluationState: normalizedEvaluationState,
            loaded: true,
          });
          lastLoadedFetchKey = requestKey;

          logPolicyCacheToConsole(policies, { studyScope: studyScope || null });

          if (myGen !== fetchPoliciesGeneration) return;
          set({ loading: false, loadingScope: null, initialLoadComplete: true });
        } catch (err) {
          if (myGen !== fetchPoliciesGeneration) return;
          // A silent (background) refresh must never clear the cache: a transient
          // network blip would otherwise revoke every permission in the UI at once.
          // Keep what we have and let the next refresh correct it.
          if (!silent) {
            set({
              policies: {},
              actionParents: {},
              evaluationState: null,
              loaded: true,
            });
          }
          console.warn("[Policy cache] load failed", err?.message || err);
          if (myGen !== fetchPoliciesGeneration) return;
          if (!silent) {
            set({ loading: false, loadingScope: null, initialLoadComplete: true });
          }
        }
      };

      inFlightFetchKey = requestKey;
      inFlightFetchPromise = run().finally(() => {
        if (inFlightFetchPromise) {
          inFlightFetchKey = "";
          inFlightFetchPromise = null;
        }
      });
      return inFlightFetchPromise;
    },

    clear: () => {
      fetchPoliciesGeneration += 1;
      set({
        policies: {},
        actionParents: {},
        evaluationState: null,
        loaded: false,
        loading: false,
        loadingScope: null,
        initialLoadComplete: false,
      });
      inFlightFetchKey = "";
      inFlightFetchPromise = null;
      lastLoadedFetchKey = "";
    },

    can: (action) => {
      const raw = String(action || "").trim();
      if (!raw) return false;
      const { policies = {}, actionParents = {} } = get();
      if (policies?.[raw] !== true) return false;

      const parents = actionParents?.[raw] || [];
      for (const parentAction of parents) {
        if (get().can(parentAction) !== true) return false;
      }
      return true;
    },

    /**
     * Tab / sidebar visibility — matches GET /policy/me (_skipParentCheck: true).
     * Does not walk actionParents (stage actions should keep using can()).
     */
    canTab: (action) => {
      const raw = String(action || "").trim().toLowerCase();
      if (!raw) return false;
      const { policies = {} } = get();
      if (policies[raw] === true) return true;
      const colon = raw.indexOf(":");
      if (colon > 0) {
        const module = raw.slice(0, colon);
        return policies[module] === true;
      }
      return false;
    },
  };
});
